/**
 * ExternalLoginPage — branded login page for external portal views.
 *
 * Route: /ext/:token/login
 *
 * Flow:
 *   1. Read view token from URL params
 *   2. Fetch theme via GET /api/ext/{token}/meta (no auth required)
 *   3. Inject CSS variables before render (FOUC prevention)
 *   4. Show ExternalLoginPanel with tenant branding
 *   5. If token invalid/expired → show clear error (AC10)
 *   6. After auth → redirect to /ext/{token}
 *
 * planTEK Phase 10d.4 — Branded portal entry experience (Revision 2).
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { injectCSSVariables, DEFAULT_THEME } from '@/lib/theme';
import type { TenantTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLoginPanel } from '@/components/external/ExternalLoginPanel';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';

export function ExternalLoginPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [themeReady, setThemeReady] = useState(false);
  const [branding, setBranding] = useState<TenantTheme['branding'] | undefined>();
  const [viewName, setViewName] = useState('');
  // null = unknown / error; true = confirmed invalid/expired (404)
  const [tokenInvalid, setTokenInvalid] = useState(false);

  // Fetch and inject tenant theme before rendering (FOUC prevention).
  // Uses the public /meta endpoint keyed by view token — no auth required.
  // If the endpoint returns null (token not found / expired), flag it so we
  // can show a clear error message per AC10.
  useEffect(() => {
    if (!token) {
      injectCSSVariables(DEFAULT_THEME);
      setThemeReady(true);
      return;
    }

    api.externalViews.getPublicMeta(token)
      .then((meta) => {
        if (!meta) {
          // Token not found or expired — show error state
          injectCSSVariables(DEFAULT_THEME);
          setTokenInvalid(true);
        } else {
          if (meta.theme) {
            injectCSSVariables(meta.theme);
            setBranding(meta.theme.branding ?? undefined);
          } else {
            injectCSSVariables(DEFAULT_THEME);
          }
          if (meta.view?.name) {
            setViewName(meta.view.name);
          }
        }
      })
      .catch(() => {
        // Network error — use default theme but still show login form
        injectCSSVariables(DEFAULT_THEME);
      })
      .finally(() => {
        setThemeReady(true);
      });
  }, [token]);

  // After authentication, redirect back to the view.
  useEffect(() => {
    if (!isAuthenticated || authLoading || !token) return;
    navigate(`/ext/${token}`, { replace: true });
  }, [isAuthenticated, authLoading, token, navigate]);

  // Hold skeleton until theme is injected and Firebase auth is initialised.
  if (!themeReady || authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        data-testid="ext-login-loading"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <div className="w-full max-w-sm space-y-4">
          <Skeleton className="h-14 w-14 rounded-2xl mx-auto" />
          <Skeleton className="h-6 w-48 mx-auto" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // Already authenticated — redirect fires on next tick, render nothing.
  if (isAuthenticated) return null;

  // AC10: Invalid or expired token — show clear error with branded footer.
  if (tokenInvalid || !token) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        data-testid="ext-login-invalid-token"
        style={{ backgroundColor: 'var(--background, #f9fafb)' }}
      >
        <div className="max-w-sm w-full">
          <div
            className="rounded-2xl border shadow-sm p-8 space-y-4 text-center"
            style={{
              backgroundColor: 'var(--card, var(--background))',
              borderColor: 'var(--border, #e5e7eb)',
            }}
          >
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto"
              style={{ backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 15%, transparent)' }}
            >
              <svg
                className="w-6 h-6"
                style={{ color: 'var(--destructive, #dc2626)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1
              className="text-lg font-semibold"
              style={{ color: 'var(--foreground)' }}
              data-testid="invalid-token-heading"
            >
              Link expired or invalid
            </h1>
            <p
              className="text-sm"
              style={{ color: 'var(--muted-foreground, #6b7280)' }}
              data-testid="invalid-token-message"
            >
              This portal link has expired or is no longer valid. Please contact your account manager for a new invitation.
            </p>
          </div>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  return (
    <ExternalLoginPanel
      viewName={viewName}
      branding={branding}
    />
  );
}
